import GamePreferences from '@/components/game-play/GamePreferences';
import GameFormComponent from '@/components/game-setup/GameFormComponent';
import GolfIcon from '@/components/GolfIcon';
import PlayerComponent from '@/components/player/PlayerComponent';
import { api } from '@/utils/api';
import { supabase } from '@/utils/supabase';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonRouter,
  useIonToast
} from '@ionic/react';
import { arrowBackOutline, chevronForward, optionsOutline, peopleOutline, settingsOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const GameSettings: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { t } = useTranslation();
  const router = useIonRouter();
  const [presentToast] = useIonToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'game-settings' | 'players' | 'preferences'>('menu');
  const [gameData, setGameData] = useState<any>(null);

  // Load game data on component mount
  useEffect(() => {
    loadGameData();

    // Subscribe to game settings updates
    const subscription = supabase
      .channel('game_settings_updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'game_settings_updates',
        filter: `game_id=eq.${gameId}`,
      }, () => {
        // Refresh game data when settings are updated by another player
        handleRefreshGameData();
      })
      .subscribe();

    return () => {
      // Clean up the subscription when the component unmounts
      subscription.unsubscribe();
    };
  }, [gameId]);

  const loadGameData = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/game/${gameId}`);
      setGameData(response.data);
    } catch (error) {
      console.error('Error loading game data:', error);
      presentToast({
        message: t('error_loading_game'),
        duration: 3000,
        color: 'danger'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshGameData = async () => {
    setIsRefreshing(true);
    try {
      // Call the API to get the latest game data
      const response = await api.get(`/game/${gameId}`);
      setGameData(response.data);

      presentToast({
        message: t('game_settings_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error refreshing game data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle back button actions
  const handleBack = () => {
    if (activeView === 'menu') {
      router.push(`/play/${gameId}`);
    } else {
      setActiveView('menu');
    }
  };

  const handleUpdateSuccess = () => {
    // Notify other players that the game settings have been updated
    presentToast({
      message: t('settings_saved'),
      duration: 2000,
      color: 'success'
    });

    handleRefreshGameData();
    setActiveView('menu');
  };

  const renderContent = () => {
    if (isLoading) {
      return ''
    }

    switch (activeView) {
      case 'game-settings':
        return (
          <GameFormComponent
            gameId={gameId}
            initialData={gameData}
            onBack={handleBack}
            onUpdateSuccess={handleUpdateSuccess}
          />
        );

      case 'players':
        return (
          <>
            <IonHeader className="ion-no-border">
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => setActiveView('menu')}>
                    <IonIcon icon={arrowBackOutline} />
                  </IonButton>
                </IonButtons>
                <IonTitle>{t('players')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              {gameData?.players && (
                <PlayerComponent
                  players={gameData.players}
                  gameId={gameId}
                  course={gameData.course}
                  isSetupMode={false}
                  onUpdateSuccess={handleRefreshGameData}
                />
              )}
            </IonContent>
          </>
        );

      case 'preferences':
        return (
          <>
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => setActiveView('menu')}>
                    {t('back')}
                  </IonButton>
                </IonButtons>
                <IonTitle>{t('preferences')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <GamePreferences
                gameId={gameId}
                preferences={gameData?.preferences}
                onUpdateSuccess={handleRefreshGameData}
              />
            </IonContent>
          </>
        );

      default: // Menu
        return (
          <>
            <IonHeader className='ion-no-border'>
              <IonToolbar color="secondary">
                <IonButtons slot="start">
                  <IonButton onClick={handleBack}>
                    {t('back')}
                  </IonButton>
                </IonButtons>
                <IonTitle>{t('game_settings')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent color="light">
              <div className="p-4 max-w-3xl mx-auto">
                {gameData && (
                  <IonItem lines='none' className="bg-card flex flex-col rounded-xl p-4 mb-6">
                    <div>
                      <div className="font-semibold text-lg mb-1">{gameData.game?.clubName || t('game')}</div>
                      <div className="text-sm text-muted-foreground">{gameData.game?.courseName || ''}</div>
                      <div className="flex items-center mt-2">
                        <GolfIcon name="calendar" className="text-sm text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {gameData.game?.date ? new Date(gameData.game.date).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </div>
                  </IonItem>
                )}

                <IonList lines="none" className="rounded-md bg-card">
                  <IonItem button onClick={() => setActiveView('game-settings')} detail={false} lines="full">
                    <IonIcon slot="start" icon={settingsOutline} color="primary" />
                    <IonLabel>{t('game_settings')}</IonLabel>
                    <IonIcon slot="end" icon={chevronForward} />
                  </IonItem>

                  <IonItem button onClick={() => setActiveView('players')} detail={false} lines="full">
                    <IonIcon slot="start" icon={peopleOutline} color="primary" />
                    <IonLabel>{t('players')} ({gameData?.players?.length})</IonLabel>
                    <IonIcon slot="end" icon={chevronForward} />
                  </IonItem>

                  <IonItem button onClick={() => setActiveView('preferences')} detail={false}>
                    <IonIcon slot="start" icon={optionsOutline} color="primary" />
                    <IonLabel>{t('settings')}</IonLabel>
                    <IonIcon slot="end" icon={chevronForward} />
                  </IonItem>

                </IonList>
                <div className="m-2">
                  ID: {gameData.game.id}
                </div>

              </div>
            </IonContent>
          </>
        );
    }
  };

  return (
    <IonPage>
      <IonLoading isOpen={isRefreshing} message={t('refreshing_game')} />
      {renderContent()}
    </IonPage>
  );
};

export default GameSettings;
